import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export interface ReversrContextValue {
  history: any[];
  currentIndex: number;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  recordChange: (change: any) => void;
  clearHistory: () => void;
}

export const ReversrContext = createContext<ReversrContextValue | undefined>(undefined);

export function useReversr() {
  const context = useContext(ReversrContext);
  if (!context) {
    throw new Error('useReversr must be used within ReversrProvider');
  }
  return context;
}

export interface ReversrProviderProps {
  children: React.ReactNode;
  maxHistory?: number;
}

export function ReversrProvider({ children, maxHistory = 50 }: ReversrProviderProps) {
  const [history, setHistory] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  const recordChange = useCallback((change: any) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, currentIndex + 1);
      newHistory.push(change);
      if (newHistory.length > maxHistory) {
        newHistory.shift();
      }
      return newHistory;
    });
    setCurrentIndex(prev => Math.min(prev + 1, maxHistory - 1));
  }, [currentIndex, maxHistory]);

  const undo = useCallback(() => {
    if (canUndo) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [canUndo]);

  const redo = useCallback(() => {
    if (canRedo) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [canRedo]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setCurrentIndex(-1);
  }, []);

  const value: ReversrContextValue = {
    history,
    currentIndex,
    canUndo,
    canRedo,
    undo,
    redo,
    recordChange,
    clearHistory
  };

  return (
    <ReversrContext.Provider value={value}>
      {children}
    </ReversrContext.Provider>
  );
}

export function useFieldHistory(fieldName: string) {
  const { recordChange, undo, redo, canUndo, canRedo } = useReversr();
  const previousValue = useRef<any>(undefined);

  const trackChange = useCallback((newValue: any) => {
    if (previousValue.current !== newValue) {
      recordChange({
        field: fieldName,
        before: previousValue.current,
        after: newValue,
        timestamp: Date.now()
      });
      previousValue.current = newValue;
    }
  }, [fieldName, recordChange]);

  return {
    trackChange,
    undo,
    redo,
    canUndo,
    canRedo
  };
}

export default ReversrProvider;
